<template>
  <CardLayout title="History">
    <v-skeleton-loader v-if="isLoading" type="list-item-avatar-two-line" />
    <v-timeline v-else-if="taskHistory && taskHistory.length" dense>
      <v-timeline-item
        v-for="item in taskHistory"
        :key="item.id"
        :color="getTimelineColor(item)"
        small
      >
        <v-card class="elevation-1">
          <v-card-title class="subtitle-2 d-flex align-center">
            <CardUserAvatar :user="item.user" />
            {{ formatUserName(item.user) }}
            {{ getItemActionText(item) }}
            <span class="text-caption grey--text ml-2">
              {{ formatDateTime4Humans(item.insertedAt) }}
            </span>
          </v-card-title>
          <v-card-text>
            <div v-if="isCommentChange(item.newData)">
              <div class="comment-change">
                <strong>{{ getCommentActionText(item.newData) }}: </strong>
                <div class="mt-1 pa-2 grey lighten-4 rounded">
                  "{{ getCommentContent(item.newData) }}"
                </div>
              </div>
            </div>
            <div v-else-if="isAttachmentChange(item.newData)">
              <div class="attachment-change">
                <strong>{{ getAttachmentActionText(item.newData) }}: </strong>
                <div class="mt-1">
                  <v-icon small>mdi-file-document</v-icon>
                  {{ getAttachmentName(item.newData) }}
                </div>
              </div>
            </div>
            <div v-else-if="item.changeset">
              <div
                v-for="(change, field) in filteredChangeset(item.changeset)"
                :key="field"
              >
                <strong>{{ formatFieldName(field) }}: </strong>
                <span :style="getStatusColor(change.old)" class="pa-1">{{
                  field.toLowerCase() === "updated_by"
                    ? getUpdatedByName(change.old, item)
                    : titleCase(change.old) || "(empty)"
                }}</span>
                <v-icon small>mdi-arrow-right-thin</v-icon>
                <span :style="getStatusColor(change.new)" class="pa-1">{{
                  field.toLowerCase() === "updated_by"
                    ? getUpdatedByName(change.new, item)
                    : titleCase(change.new) || "(empty)"
                }}</span>
              </div>
            </div>
            <div v-else-if="item.operation === 0">Task created</div>
          </v-card-text>
        </v-card>
      </v-timeline-item>
    </v-timeline>
    <v-alert v-else type="info" outlined>
      No history available for this task.
    </v-alert>
  </CardLayout>
</template>

<script>
import CardLayout from "@layout/CardLayout.vue";
import CardUserAvatar from "@components/common/CardUserAvatar.vue";
import { TASK_HISTORY } from "./graphql";
import { titleCase } from "@tod-ui/helpers/strings";
import { formatName } from "./helper";
import { formatDateTime4Humans } from "@tod-ui/helpers/datetime";
import useUser from "@components/authentication/useUser";
import { RESOLUTION_STATUS } from "@tod-ui/constants/task_management";

export default {
  name: "CardTaskHistory",
  components: {
    CardLayout,
    CardUserAvatar,
  },
  props: {
    taskId: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { defaultOrganization } = useUser();
    return { defaultOrganization };
  },
  data() {
    return {
      userLookupMap: {},
      taskHistory: [],
    };
  },
  computed: {
    isLoading() {
      return this.$apollo.queries.taskHistory.loading;
    },
  },
  apollo: {
    taskHistory: {
      query: TASK_HISTORY,
      variables() {
        return { taskId: this.taskId };
      },
      result({ data }) {
        if (data?.taskHistory) {
          this.taskHistory = data.taskHistory;
          this.buildUserLookupMap(data.taskHistory);

          const creationEvent = data.taskHistory.find(
            (item) => item.operation === 0
          );
          if (
            creationEvent &&
            creationEvent.newData &&
            creationEvent.newData.incident_id
          ) {
            this.$emit("incident-found", creationEvent.newData.incident_id);
          }
        }
      },
    },
  },
  methods: {
    getTimelineColor(item) {
      if (this.isCommentChange(item.newData)) {
        return "success";
      }
      return item.operation === 0 ? "success" : "primary";
    },
    getItemActionText(item) {
      if (this.isCommentChange(item.newData)) {
        return "commented on";
      }
      if (this.isAttachmentChange(item.newData)) {
        return "modified attachments on";
      }
      if (item.operation === 0) {
        return "created the task";
      }
      const field = Object.keys(item.changeset || {})[0];
      return `updated the ${this.formatFieldName(field)}`;
    },
    isCommentChange(newData) {
      return (
        newData &&
        newData.events &&
        newData.events.some(
          (event) =>
            event.type &&
            (event.type.includes("comment_created") ||
              event.type.includes("comment_deleted"))
        )
      );
    },
    isAttachmentChange(newData) {
      return (
        newData &&
        newData.events &&
        newData.events.some(
          (event) =>
            event.type &&
            (event.type.includes("attachment_created") ||
              event.type.includes("attachment_deleted"))
        )
      );
    },
    getCommentActionText(newData) {
      if (newData && newData.events) {
        const commentEvent = newData.events.find(
          (event) => event.type && event.type.includes("comment_")
        );
        if (commentEvent) {
          return commentEvent.type.includes("created")
            ? "Added comment"
            : "Deleted comment";
        }
      }
      return "Updated comment";
    },
    getCommentContent(newData) {
      if (newData && newData.events) {
        const commentEvent = newData.events.find(
          (event) => event.type && event.type.includes("comment_")
        );
        return (
          commentEvent?.data?.comment?.content ||
          "Comment content not available"
        );
      }
      return "Comment content not available";
    },
    getAttachmentActionText(newData) {
      if (newData && newData.events) {
        const attachmentEvent = newData.events.find(
          (event) => event.type && event.type.includes("attachment_")
        );
        if (attachmentEvent) {
          return attachmentEvent.type.includes("created")
            ? "Added attachment"
            : "Deleted attachment";
        }
      }
      return "Updated attachment";
    },
    getAttachmentName(newData) {
      if (newData && newData.events) {
        const attachmentEvent = newData.events.find(
          (event) => event.type && event.type.includes("attachment_")
        );
        return attachmentEvent?.data?.attachment?.name || "Unknown file";
      }
      return "Unknown file";
    },
    formatFieldName(field) {
      if (!field) {
        return "";
      }
      if (field.toLowerCase() === "resolution") {
        return "Status";
      }
      return field
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    filteredChangeset(changeset) {
      const filtered = { ...changeset };
      delete filtered.updated_at;
      delete filtered.actions;
      delete filtered.events;
      return filtered;
    },
    titleCase,
    formatUserName(user) {
      return formatName(user);
    },
    formatDateTime4Humans(date) {
      return formatDateTime4Humans(date, this.defaultOrganization.timeZone);
    },
    getStatusColor(status) {
      let color = "";
      switch (status) {
        case RESOLUTION_STATUS.TODO:
          color = "105,189,217";
          break;
        case RESOLUTION_STATUS.IN_PROGRESS:
          color = "255,152,0";
          break;
        case RESOLUTION_STATUS.IN_REVIEW:
          color = "156,39,176";
          break;
        case RESOLUTION_STATUS.DONE:
          color = "66,171,86";
          break;
        case RESOLUTION_STATUS.CANCELLED:
          color = "158,158,158";
          break;
        case RESOLUTION_STATUS.REOPENED:
          color = "211,47,47";
          break;
      }
      return color
        ? {
            backgroundColor: `rgba(${color}, 0.2)`,
            color: `rgb(${color})`,
          }
        : {};
    },
    buildUserLookupMap(taskHistory) {
      this.userLookupMap = {};

      taskHistory.forEach((item) => {
        if (item.user?.id) {
          this.userLookupMap[item.user.id] = {
            firstName: item.user.firstName,
            lastName: item.user.lastName,
          };
        }

        if (item.changeset?.events?.new) {
          item.changeset.events.new.forEach((event) => {
            if (event.data?.user?.id) {
              this.userLookupMap[event.data.user.id] = {
                firstName: event.data.user.first_name,
                lastName: event.data.user.last_name,
              };
            }
          });
        }
      });
    },

    getUpdatedByName(userId, historyItem) {
      if (historyItem.user && historyItem.user.id === userId) {
        return formatName(historyItem.user);
      }

      if (this.userLookupMap[userId]) {
        return formatName(this.userLookupMap[userId]);
      }

      return "Unknown User";
    },
  },
};
</script>

<style scoped>
.comment-change {
  border-left: 3px solid #2196f3;
  padding-left: 8px;
}

.attachment-change {
  border-left: 3px solid #ff9800;
  padding-left: 8px;
}

.comment-change .grey.lighten-4 {
  border-radius: 4px !important;
}
</style>
